import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";

import { EventOrigin, Events, SectionMode, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import type { ToolbarButtonEntry } from "contexts/toolbars";
import { toolbarsActions, useDispatchToolbars, useToolbarButtons } from "contexts/toolbars";
import { Picker, renderActions, selectPicker } from "features/render";
import CoordinatesIcon from "media/icons/coordinates-mobile.svg?react";
// import DimensionIcon from "media/icons/dimension.svg?react";
// import LaserIcon from "media/icons/laser.svg?react";
// import ManholeIcon from "media/icons/manhole.svg?react";
import PointToPointIcon from "media/icons/point-to-point.svg?react";
import SectionPlaneIcon from "media/icons/section-plane.svg?react";
import TapeMeasureIcon from "media/icons/tape-measure.svg?react";
import { selectObjectMeasureEnabled } from "slices/explorer";
import { ProcoreToolMode } from "slices/procoreUx";

import { BottomToolbarButton } from "./BottomToolbarButton";

interface BottomToolbarToolControlsContentProps {
    toolId: ProcoreToolMode;
}

/**
 * Tool-specific controls shown when a tool is active
 */
export function BottomToolbarToolControlsContent({ toolId }: BottomToolbarToolControlsContentProps) {
    switch (toolId) {
        case ProcoreToolMode.Measure:
            return <MeasureControls />;
        case ProcoreToolMode.Markup:
            return <MockControls label="Markup" />;
        case ProcoreToolMode.Create:
            return <MockControls label="Create" />;
        case ProcoreToolMode.Section:
            return <SectionControls />;
        default:
            return <MockControls label={toolId} />;
    }
}

interface MeasureButtonRendererProps {
    toolbarId: string;
}

/**
 * Custom button renderer for measure controls
 */
function MeasureButtonRenderer({ toolbarId }: MeasureButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);
    const currentPicker = useAppSelector(selectPicker);

    if (buttons.length === 0) {
        return null;
    }

    return (
        <Box display="flex" gap={1}>
            {buttons.map((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (config.separator || !state.visible) {
                    return null;
                }

                // Active state comes from current picker, not toolbar state
                const pickerValue = parseInt(config.id.replace("picker-", ""), 10) as Picker;
                const isActive = currentPicker === pickerValue;

                return (
                    <BottomToolbarButton
                        key={config.id}
                        icon={config.icon}
                        label={config.title || ""}
                        tooltip={config.tooltip?.toolName || config.title || ""}
                        active={isActive}
                        onClick={config.onClick}
                    />
                );
            })}
        </Box>
    );
}

/**
 * Measure tool controls - connected to actual measurement pickers
 * TODO: Extract to per-tool file when tools are implemented
 */
function MeasureControls() {
    const dispatch = useAppDispatch();
    const toolbarDispatch = useDispatchToolbars();
    const currentPicker = useAppSelector(selectPicker);
    const isObjectMeasureEnabled = useAppSelector(selectObjectMeasureEnabled);

    const handlePickerClick = useCallback(
        (targetPicker: Picker) => {
            if (currentPicker === targetPicker) {
                dispatch(renderActions.stopPicker(targetPicker));
            } else {
                dispatch(renderActions.setPicker(targetPicker));
            }
        },
        [dispatch, currentPicker],
    );

    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            // {
            //     config: {
            //         id: `picker-${Picker.Dimension}`,
            //         icon: <DimensionIcon />,
            //         onClick: () => handlePickerClick(Picker.Dimension),
            //         title: "Dimension",
            //         tooltip: {
            //             toolName: "Dimension measurement",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            {
                config: {
                    id: `picker-${Picker.ObjectMeasurement}`,
                    icon: <TapeMeasureIcon />,
                    onClick: () => handlePickerClick(Picker.ObjectMeasurement),
                    title: "Distance",
                    tooltip: {
                        toolName: "Shortest Distance",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: isObjectMeasureEnabled,
                },
            },
            {
                config: {
                    id: `picker-${Picker.PointLine}`,
                    icon: <PointToPointIcon />,
                    onClick: () => handlePickerClick(Picker.PointLine),
                    title: "Point",
                    tooltip: {
                        toolName: "Point to Point measurement",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: !isObjectMeasureEnabled,
                },
            },
            // {
            //     config: {
            //         id: `picker-${Picker.OutlineLaser}`,
            //         icon: <LaserIcon />,
            //         onClick: () => handlePickerClick(Picker.OutlineLaser),
            //         title: "Laser",
            //         tooltip: {
            //             toolName: "Laser measurement",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: !isObjectMeasureEnabled,
            //     },
            // },
            // {
            //     config: {
            //         id: `picker-${Picker.Manhole}`,
            //         icon: <ManholeIcon />,
            //         onClick: () => handlePickerClick(Picker.Manhole),
            //         title: "Manhole",
            //         tooltip: {
            //             toolName: "Manhole measurement",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            {
                config: {
                    id: `picker-${Picker.Coordinates}`,
                    icon: <CoordinatesIcon />,
                    onClick: () => handlePickerClick(Picker.Coordinates),
                    title: "Coordinate",
                    tooltip: {
                        toolName: "Coordinate display",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
        ],
        [handlePickerClick, isObjectMeasureEnabled],
    );

    const toolbarId = "bottom-toolbar-measure";

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: toolbarId,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(toolbarId));
        };
    }, [toolbarDispatch, buttons]);

    return <MeasureButtonRenderer toolbarId={toolbarId} />;
}

/**
 * Section tool controls - connected to actual section pickers.
 */

interface SectionButtonRendererProps {
    toolbarId: string;
}

function SectionButtonRenderer({ toolbarId }: SectionButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);
    const currentPicker = useAppSelector(selectPicker);

    if (buttons.length === 0) {
        return null;
    }

    return (
        <Box display="flex" gap={1}>
            {buttons.map((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (config.separator || !state.visible) {
                    return null;
                }

                const isActive = currentPicker === Picker.SectionPlane;

                return (
                    <BottomToolbarButton
                        key={config.id}
                        icon={config.icon}
                        label={config.title || ""}
                        tooltip={config.tooltip?.toolName || config.title || ""}
                        active={isActive}
                        onClick={config.onClick}
                    />
                );
            })}
        </Box>
    );
}

function SectionControls() {
    const dispatch = useAppDispatch();
    const toolbarDispatch = useDispatchToolbars();
    const currentPicker = useAppSelector(selectPicker);

    const handlePickerClick = useCallback(() => {
        if (currentPicker === Picker.SectionPlane) {
            dispatch(renderActions.stopPicker(Picker.SectionPlane));
        } else {
            dispatch(renderActions.setPicker(Picker.SectionPlane));
            void track(Events.clipPlane.enabled, { mode: SectionMode.ClipPlane, origin: EventOrigin.Toolbar });
        }
    }, [dispatch, currentPicker]);

    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            {
                config: {
                    id: `picker-${Picker.SectionPlane}`,
                    icon: <SectionPlaneIcon />,
                    onClick: handlePickerClick,
                    title: "Plane",
                    tooltip: {
                        toolName: "Section plane",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
        ],
        [handlePickerClick],
    );

    const toolbarId = "bottom-toolbar-section";

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: toolbarId,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(toolbarId));
        };
    }, [toolbarDispatch, buttons]);

    return <SectionButtonRenderer toolbarId={toolbarId} />;
}

/**
 * Placeholder for tools that don't have controls yet
 */
function MockControls({ label }: { label: string }) {
    return (
        <Box display="flex" alignItems="center" px={2}>
            <Typography variant="body2" color="text.secondary">
                {label} controls (coming soon)
            </Typography>
        </Box>
    );
}
